'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  category: 'discovery' | 'partner' | 'general';
}

const ContactFAQ = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQItem[] = [
    {
      question: 'How long does a discovery call typically last?',
      answer: 'Discovery calls are scheduled for 30 minutes. This gives us enough time to understand your AI transformation challenges, discuss your goals, and outline potential next steps without taking too much of your valuable time.',
      category: 'discovery'
    },
    {
      question: 'What should I prepare before the discovery call?',
      answer: 'We recommend having a general understanding of your current AI initiatives, key challenges you\'re facing, and your transformation timeline. However, don\'t worry if you\'re just starting to explore AI adoption—we\'ll guide you through the conversation.',
      category: 'discovery'
    },
    {
      question: 'Is there any cost for the discovery call?',
      answer: 'No, discovery calls are completely free with no obligation. They\'re designed to help you understand how we can support your AI transformation journey and determine if we\'re the right fit for your needs.',
      category: 'discovery'
    },
    {
      question: 'What are the requirements to join the partner network?',
      answer: 'We look for transformation specialists with proven experience in change management, AI implementation, or organizational development. Ideal partners have 5+ years of relevant experience, strong client references, and alignment with our human-centered approach to AI transformation.',
      category: 'partner'
    },
    {
      question: 'How does the partner collaboration model work?',
      answer: 'Partners work on client engagements using our proven methodologies and frameworks. We handle client acquisition, project scoping, and quality assurance, while partners deliver transformation services. Revenue sharing is transparent and competitive.',
      category: 'partner'
    },
    {
      question: 'What support do partners receive?',
      answer: 'Partners gain access to our methodology library, training resources, client engagement frameworks, and ongoing professional development. You\'ll also join a community of transformation specialists for knowledge sharing and collaboration.',
      category: 'partner'
    },
    {
      question: 'How quickly will I receive a response to my enquiry?',
      answer: 'We aim to respond to all enquiries within 24 hours during business days (Monday-Friday, 9:00-17:00 GMT). For urgent matters, please call our office directly at +44 20 7123 4567.',
      category: 'general'
    },
    {
      question: 'Can I schedule a call outside of UK business hours?',
      answer: 'Yes, we can accommodate calls outside standard UK hours for international clients. Please mention your timezone and preferred times in your booking request, and we\'ll do our best to find a suitable slot.',
      category: 'general'
    },
    {
      question: 'Do you offer in-person consultations?',
      answer: 'Yes, we offer both virtual and in-person consultations. Our London office is available for meetings, and we can travel to client locations for larger engagements. Initial discovery calls are typically conducted virtually for convenience.',
      category: 'general'
    },
    {
      question: 'How is my personal data protected?',
      answer: 'We take data protection seriously and comply fully with GDPR regulations. All information submitted through our contact forms is encrypted, stored securely, and used only for the purposes you\'ve consented to. You can request data deletion at any time.',
      category: 'general'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-12 lg:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="space-y-3 mt-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 lg:py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-headline text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Find answers to common questions about our contact process, discovery calls, and partnership opportunities.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-card rounded-lg shadow-card border border-border overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left hover:bg-muted/50 transition-colors duration-300"
                >
                  <span className="font-semibold font-headline text-foreground pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    variant="outline"
                    className={`flex-shrink-0 text-primary transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-2">
                    <p className="text-muted-foreground font-body leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-primary/5 rounded-lg p-8 text-center">
            <h3 className="text-xl font-bold font-headline text-primary mb-3">
              Still Have Questions?
            </h3>
            <p className="text-muted-foreground font-body mb-6">
              Can't find the answer you're looking for? Our team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+442071234567"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold font-cta text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300"
              >
                <Icon name="PhoneIcon" size={20} variant="outline" />
                Call Us
              </a>
              <a
                href="mailto:hello@aichangeleaders.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold font-cta text-sm text-primary bg-background border-2 border-primary hover:bg-primary hover:text-white transition-all duration-300"
              >
                <Icon name="EnvelopeIcon" size={20} variant="outline" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFAQ;